import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { useStore } from 'react-redux';
import { useIsomorphicLayoutEffect, useRouter, useServices } from 'core/common/hooks';
import { RootInitialState } from 'core/common/store';
import { cloneDeep } from 'core/common/utils/cloneDeep';
import { useReportWebVitals } from 'core/common/web-vitals/hooks';
import { FeatureFlagsConfig } from 'core/feature-flags/entities';
import { useFeatureFlags } from 'core/feature-flags/hooks';
import { Flow } from 'core/flow/entities';
import { FunnelConfig } from 'core/funnel/entities';
import { getConditionAttributes } from 'core/funnel/store';
import i18n from 'core/i18n';
import { useLanguage } from 'core/localization/hooks';
import { useAnalyticsIds, useInitBingAds } from 'core/scripts/hooks';

type FunnelContextData = {
  flow: Flow;
  analyticsIds: {
    facebook: Array<string>;
    tikTok: Array<string>;
  };
} & Omit<FunnelConfig, 'flows' | 'analytics'>;

export const FunnelContext = createContext<FunnelContextData>(undefined!);

type FunnelProviderProps = {
  children: ReactNode;
  resources: FunnelConfig;
  features: FeatureFlagsConfig['features'];
};

export const FunnelProvider = ({ children, resources, features }: FunnelProviderProps) => {
  const { locale = 'en' } = useRouter();

  const [featuresUpdated, setFeaturesUpdated] = useState(false);

  const featureFlags = useFeatureFlags();

  const { analyticsService, funnelSettingsService } = useServices();

  const store = useStore<RootInitialState>();

  const { language } = useLanguage();

  const { flows, analytics, ...quizWithoutFlows } = resources;

  const activeFlow: Flow = flows[0];

  const funnelConditions = getConditionAttributes(store.getState());

  const analyticsPixelIds = useAnalyticsIds({
    funnelAnalytics: analytics,
    funnelConditions,
  });

  useReportWebVitals();
  useInitBingAds(analyticsPixelIds.bingAds.combined);

  useIsomorphicLayoutEffect(() => {
    analyticsService.funnelOpened({ funnelName: resources.name, funnelLocale: locale });
    funnelSettingsService.setFunnelName(resources.name);
    funnelSettingsService.setFunnelLocale(locale);
  }, [funnelSettingsService, resources.name, locale]);

  useEffect(() => {
    analyticsService.trackUserLanguage(language);
  }, [analyticsService, language]);

  useEffect(() => {
    if (!activeFlow) return;

    analyticsService.flowOpened(activeFlow.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFlow]);

  /**
   * @deprecated Use funnel instead of this
   */
  if (!featureFlags.getAttributes().quizName) {
    featureFlags.setAttributes({ quizName: resources.name });
  }

  if (!featureFlags.getAttributes().funnel) {
    featureFlags.setAttributes({ funnel: resources.name });
  }

  if (features && !featuresUpdated) {
    // TODO: implement logic to avoid using of "setFeatures" method
    featureFlags.setFeatures(features);
    setFeaturesUpdated(true);

    return null;
  }

  if (activeFlow && activeFlow.translations) {
    i18n.addResourceBundle(
      activeFlow.language,
      'translation',
      // in cause when activeFlow.translations object stored in redux
      // all objects have been frozen by immer https://immerjs.github.io/immer/freezing/
      // addResourceBundle method can't work properly with frozen object
      cloneDeep(activeFlow.translations),
      true,
      true,
    );
  }

  return (
    <FunnelContext.Provider
      value={{
        ...quizWithoutFlows,
        flow: activeFlow,
        analyticsIds: {
          facebook: analyticsPixelIds.facebook.combined,
          tikTok: analyticsPixelIds.tikTok.combined,
        },
      }}
    >
      {children}
    </FunnelContext.Provider>
  );
};

export const useFunnelData = () => {
  const context = useContext(FunnelContext);

  if (!context) {
    throw new Error('useFunnelData must be used within a FunnelProvider');
  }

  return context;
};
